/**
 * Sentry Types. These may not be accurate but we just need typescript happy
 */
interface SentryScope {
	setFingerprint(fingerprint: any[]): void;

	// Debug, error,or other...
	setLevel(level: string): void;
	setExtra(key: string, value: string): void;
}

interface SentryHub {
	onLoad(callback: () => void): void;
	init(settings: any): void;
	withScope(cb: (scope: SentryScope) => void): void;
	captureException(ex: Error): void;
	captureMessage(message: string, level?: string): void;
	getCurrentHub(): SentryHub;
}

declare const Sentry: SentryHub;

function initSentry(release: string, dsn: string) {
	const isStagingSite = /^(woo|qa|demo|ui-test|theme\d*)\.peachpay\.app/i;
	const isLocalSite = /^(store\.local|localhost|127\.0\.0\.1)/i;

	const isDevelopmentMode = isStagingSite.test(location.hostname) || isLocalSite.test(location.hostname);

	try {
		Sentry.onLoad(() => {
			Sentry.init({
				dsn,
				environment: isDevelopmentMode ? 'development' : 'production',
				release,
				beforeSend(event: any, hint: any) {
					// This is to prevent receiving errors from Google Recaptcha which are not relevant to us: https://github.com/getsentry/sentry-javascript/issues/2514#issuecomment-603971338
					if (hint.originalException === 'Timeout') {
						return null;
					}

					// This prevents collecting "Non-Error promise rejection captured with value: undefined". These are caused by third party scripts and are not relevant to us which throw non
					// Error instances.
					if (hint.originalException === 'undefined' || hint.originalException === undefined) {
						return null;
					}

					// eslint-disable-next-line @typescript-eslint/no-unsafe-return
					return event;
				},
				// Ignore errors produced by Chrome, Firefox, and Safari upon
				// navigating away from a page that has a fetch request in progress.
				// See https://forum.sentry.io/t/typeerror-failed-to-fetch-reported-over-and-overe/8447/2
				ignoreErrors: [
					'TypeError: Failed to fetch',
					'TypeError: NetworkError when attempting to fetch resource.',
					'TypeError: cancelled',
					'TypeError: cancelado',
					'TypeError: Abgebrochen',
					'TypeError: annulé',
					'Window navigated away',
					'annullato',
					'Load failed',
				],
				allowUrls: [
					/peachpay-for-woocommerce/i,
				],
			});
		});
	} catch {
	// Sentry is not present. Do not make things worse!
	}
}

/**
 * Used to capture a exception with sentry
 *
 * @param error The error/exception to report
 * @param extra Details to include with the sentry report
 * @param fingerprint Fingerprint to identify a sequence of events?
 */
function captureSentryException(error: Error, extra?: Record<string, any> | null, fingerprint?: any[] | null) {
	try {
		Sentry.withScope(scope => {
			if (extra) {
				// Attempt extras
				try {
					Object.entries(extra).forEach(([key, value]) => {
						scope.setExtra(key, value);
					});
				} catch {
					// Do no harm.
				}
			}

			if (fingerprint) {
				// Attempt Fingerprint
				try {
					scope.setFingerprint(fingerprint);
				} catch {
					// Do no harm.
				}
			}

			// Capture exception with any above set extras and/or fingerprint
			Sentry.captureException(error);
		});
	} catch {
		// Sentry is not present. Don't make things worse.
	}
}

/**
 * Captures a message of some sort of application event. This can be an
 * error or just a situation the application encountered that may need
 * reported.
 */
function captureSentryEvent(eventMessage: string) {
	try {
		Sentry.captureMessage(eventMessage);
	} catch {
		// Sentry is not present. Don't make things worse.
	}
}

export {
	initSentry,
	captureSentryEvent,
	captureSentryException,
};
